import { isVideo } from "../../../utils";
import TemplatePostActions from "../../TemplatePostActions";
import { getVideoDuration } from "../utils";

/**
 * Updates the video-related properties in the inputProps and ensures the correct template is used.
 * Also, it checks for video creatives with null media assets and updates them accordingly.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.inputProps - The input properties for the post.
 * @param {Array} params.creatives - An array of creative assets.
 * @param {Array} params.mediaAssets - An array of media assets corresponding to the creatives.
 * @param {Function} params.updatePost - Function to update the post state.
 *
 * @returns {Promise<void>} Resolves when all video assets have been updated.
 */
const useVideoBackComp = async ({ inputProps, creatives, mediaAssets, updatePost }) => {
  const isOldVideoCampaign =
    inputProps &&
    inputProps.backgroundImage &&
    isVideo(inputProps.backgroundImage) &&
    !inputProps.video;

  if (isOldVideoCampaign) {
    const defaultOrientation = "portrait";
    const duration = await getVideoDuration(inputProps.backgroundImage);

    const updatedInputProps = {
      ...inputProps,
      video: inputProps.backgroundImage,
      orientation: defaultOrientation,
      duration
    };

    await updateVideoMediaAsset({
      creatives,
      mediaAssets,
      inputProps: updatedInputProps,
      updatePost
    });

    TemplatePostActions.updateInputProps(updatedInputProps);
    TemplatePostActions.updateTemplateId("RawVideo", "video");
  }
};

/**
 * Updates media assets associated with video creatives by applying the correct template and input properties.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.creatives - An array of creative assets.
 * @param {Array} params.mediaAssets - An array of media assets corresponding to the creatives.
 * @param {Object} params.inputProps - The input properties that should be applied to the media assets.
 * @param {Function} params.updatePost - Function to update the post state.
 *
 * @returns {Promise<void>} Resolves when the media assets have been updated.
 */
const updateVideoMediaAsset = async ({ creatives, mediaAssets, inputProps, updatePost }) => {
  for (const [index, creative] of creatives.entries()) {
    if (isVideo(creative)) {
      TemplatePostActions.updateMediaAsset(mediaAssets[index], {
        inputProps,
        templateId: "RawVideo",
        type: "video",
        updatedDate: new Date().toISOString()
      });
    }
  }
};

export default useVideoBackComp;
